import { useEffect, useState, useCallback } from "react";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Spinner from "../ui/Spinner";
import TablePagination from "../common/TablePagination";
import masterService from "../../services/masterEntitiesService";
import { resolveBackendUrl } from "../../services/api";
import { toast } from "react-toastify";
import PropertyQrPosterModal from "./PropertyQrPosterModal";
import {
  QrCode,
  User,
  Car,
  Clock,
  CheckCircle,
  XCircle,
  ShieldAlert,
  LogOut,
  RefreshCw,
  Search,
  LogIn
} from "lucide-react";
import styles from "./PropertyVisitorPasses.module.css";

const STATUS_TABS = [
  { id: "ALL", label: "All Passes" },
  { id: "PENDING_HOST_APPROVAL", label: "Waiting / Pending" },
  { id: "ESCALATED_RECEPTION", label: "Escalated Reception" },
  { id: "APPROVED", label: "Approved" },
  { id: "CHECKED_IN", label: "Checked In" },
  { id: "CHECKED_OUT", label: "Checked Out" },
  { id: "REJECTED", label: "Rejected" },
  { id: "EXPIRED", label: "Expired" }
];

export default function PropertyVisitorPasses({ societyId, propertyName }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [actingId, setActingId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modal State for Override
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [submittingOverride, setSubmittingOverride] = useState(false);

  // Photo Preview Modal
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState(null);

  // QR Poster Modal
  const [qrModalOpen, setQrModalOpen] = useState(false);

  const loadVisits = useCallback(async () => {
    try {
      const data = await masterService.getIndustrialVisits(societyId);
      setVisits(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load visitor passes.");
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    if (societyId) {
      loadVisits();
      // Auto-refresh every 30 seconds for live gate monitoring
      const interval = setInterval(loadVisits, 30 * 1000);
      return () => clearInterval(interval);
    }
  }, [societyId, loadVisits]);

  const handleAction = async (id, action) => {
    setActingId(id);
    try {
      await masterService.handleVisitAction(id, action);
      toast.success(`Visitor ${action === "APPROVE" ? "Approved" : "Rejected"} successfully.`);
      loadVisits();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to process visit action.");
    } finally {
      setActingId(null);
    }
  };

  const handleCheckIn = async (id) => {
    setActingId(id);
    try {
      await masterService.checkInPass(id);
      toast.success("Visitor Checked-In successfully.");
      loadVisits();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to check-in visitor.");
    } finally {
      setActingId(null);
    }
  };

  const handleCheckOut = async (id) => {
    setActingId(id);
    try {
      await masterService.checkOutPass(id);
      toast.success("Visitor Checked-Out successfully.");
      loadVisits();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to check-out visitor.");
    } finally {
      setActingId(null);
    }
  };

  const openOverrideModal = (visit) => {
    setSelectedVisit(visit);
    setOverrideReason("");
    setOverrideModalOpen(true);
  };

  const submitOverride = async (e) => {
    e.preventDefault();
    if (!overrideReason.trim()) {
      toast.error("Please enter a mandatory override reason.");
      return;
    }

    setSubmittingOverride(true);
    try {
      await masterService.overrideVisit(selectedVisit.id, overrideReason.trim());
      toast.success("Supervisor override approved successfully!");
      setOverrideModalOpen(false);
      loadVisits();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to apply supervisor override.");
    } finally {
      setSubmittingOverride(false);
    }
  };

  const filteredVisits = visits.filter((v) => {
    if (activeTab !== "ALL") {
      const vStatus = String(v.status || "").toUpperCase();
      if (activeTab === "PENDING_HOST_APPROVAL") {
        if (!["PENDING_HOST_APPROVAL", "AWAITING_HOST", "SUBMITTED", "PENDING", "HELD_AT_GATE", "WAITING"].includes(vStatus)) {
          return false;
        }
      } else if (activeTab === "CHECKED_IN") {
        if (!["CHECKED_IN", "IN_PREMISES", "INSIDE"].includes(vStatus)) {
          return false;
        }
      } else if (activeTab === "CHECKED_OUT") {
        if (!["CHECKED_OUT", "AUTO_CHECKED_OUT", "COLLECTED"].includes(vStatus)) {
          return false;
        }
      } else if (activeTab === "APPROVED") {
        if (!["APPROVED", "ASSIGNED", "ROUTED"].includes(vStatus)) {
          return false;
        }
      } else if (vStatus !== activeTab) {
        return false;
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchName = String(v.visitor_name || "").toLowerCase().includes(q);
      const matchPhone = String(v.visitor_phone || "").includes(q);
      const matchReq = String(v.request_number || "").toLowerCase().includes(q);
      const matchPass = String(v.pass_code || "").toLowerCase().includes(q);
      const matchVehicle = String(v.vehicle_number || "").toLowerCase().includes(q);
      return matchName || matchPhone || matchReq || matchPass || matchVehicle;
    }
    return true;
  });

  const paginatedVisits = filteredVisits.slice((page - 1) * limit, page * limit);

  const getStatusBadge = (status) => {
    const raw = String(status || "").trim().toUpperCase();

    switch (raw) {
      // Stage: Yellow (Waiting / Pre-entry)
      case "AWAITING_HOST":
        return <span className={`${styles.badge} ${styles.badgeWaiting}`}>Awaiting Approval</span>;
      case "SUBMITTED":
        return <span className={`${styles.badge} ${styles.badgeWaiting}`}>Submitted</span>;
      case "PENDING_HOST_APPROVAL":
      case "PENDING":
      case "WAITING":
        return <span className={`${styles.badge} ${styles.badgeWaiting}`}>Awaiting Approval</span>;
      case "HELD_AT_GATE":
        return <span className={`${styles.badge} ${styles.badgeWaiting}`}>Held at Gate</span>;
      case "ESCALATED_DELEGATE":
        return <span className={`${styles.badge} ${styles.badgeDelegate}`}>Escalated (Delegate)</span>;
      case "ESCALATED_RECEPTION":
        return <span className={`${styles.badge} ${styles.badgeReception}`}>Escalated (Reception)</span>;

      // Stage: Blue (Approved / Assigned / Routed)
      case "APPROVED":
        return <span className={`${styles.badge} ${styles.badgeApproved}`}>Approved</span>;
      case "ASSIGNED":
        return <span className={`${styles.badge} ${styles.badgeAssigned}`}>Assigned</span>;
      case "ROUTED":
        return <span className={`${styles.badge} ${styles.badgeAssigned}`}>Routed</span>;

      // Stage: Green (Checked In / Inside Premises)
      case "CHECKED_IN":
        return <span className={`${styles.badge} ${styles.badgeInside}`}>Checked In</span>;
      case "INSIDE":
      case "IN_PREMISES":
        return <span className={`${styles.badge} ${styles.badgeInside}`}>Inside Premises</span>;

      // Stage: Grey (Checked Out / Completed)
      case "CHECKED_OUT":
        return <span className={`${styles.badge} ${styles.badgeOut}`}>Checked Out</span>;
      case "AUTO_CHECKED_OUT":
        return <span className={`${styles.badge} ${styles.badgeAutoOut}`}>Auto Checked Out</span>;
      case "COLLECTED":
        return <span className={`${styles.badge} ${styles.badgeCollected}`}>Collected at Gate</span>;

      // Stage: Red (Rejected / Terminated)
      case "REJECTED":
        return <span className={`${styles.badge} ${styles.badgeRejected}`}>Rejected</span>;
      case "EXPIRED":
        return <span className={`${styles.badge} ${styles.badgeExpired}`}>Expired</span>;
      case "CANCELLED":
        return <span className={`${styles.badge} ${styles.badgeCancelled}`}>Cancelled</span>;
      case "REVOKED":
        return <span className={`${styles.badge} ${styles.badgeRevoked}`}>Revoked</span>;

      default: {
        const sanitized = String(status || "Unknown")
          .replace(/[_-]+/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase());
        return <span className={`${styles.badge} ${styles.badgeNeutral}`}>{sanitized}</span>;
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.searchWrap}>
          <Search size={16} color="#94a3b8" />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name, phone, pass code, vehicle..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Button variant="primary" size="sm" onClick={() => setQrModalOpen(true)}>
            <QrCode size={14} /> Gate QR Poster
          </Button>
          <Button variant="outline" size="sm" onClick={loadVisits}>
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      <div className={styles.tabsRow}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabActive : ""}`}
            onClick={() => {
              setActiveTab(tab.id);
              setPage(1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : filteredVisits.length === 0 ? (
        <div className={styles.empty}>
          <QrCode size={40} color="#cbd5e1" />
          <p>No visitor passes found for the selected filter.</p>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Visitor</th>
                <th>Request & Pass Code</th>
                <th>Host & Department</th>
                <th>Vehicle & Arrival</th>
                <th>Status</th>
                <th>Timing</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedVisits.map((v) => (
                <tr key={v.id}>
                  <td>
                    <div className={styles.visitorCell}>
                      <div
                        className={styles.photoThumb}
                        onClick={() => v.photo_url && setPreviewPhotoUrl(resolveBackendUrl(v.photo_url))}
                        title="Click to zoom photo"
                      >
                        {v.photo_url ? (
                          <img src={resolveBackendUrl(v.photo_url)} alt={v.visitor_name} />
                        ) : (
                          <User size={18} color="#94a3b8" />
                        )}
                      </div>
                      <div>
                        <div className={styles.visitorName}>{v.visitor_name}</div>
                        <div className={styles.visitorPhone}>{v.visitor_phone}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.codeCell}>
                      <span className={styles.reqNumber}>{v.request_number}</span>
                      {v.pass_code && <span className={styles.passCode}>{v.pass_code}</span>}
                    </div>
                  </td>
                  <td>
                    <div className={styles.hostCell}>
                      <div className={styles.hostName}>{v.host_user?.full_name || "General Visit"}</div>
                      <div className={styles.deptName}>{v.department?.name || "-"}</div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.vehicleCell}>
                      {v.vehicle_number ? (
                        <>
                          <Car size={14} color="var(--color-dark-bg)" />
                          <span className={styles.plate}>{v.vehicle_number}</span>
                        </>
                      ) : (
                        <span className={styles.pedestrianText}>Pedestrian</span>
                      )}
                    </div>
                  </td>
                  <td>{getStatusBadge(v.status)}</td>
                  <td>
                    <div className={styles.timeCell}>
                      <Clock size={13} color="#94a3b8" />
                      <span>{new Date(v.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div className={styles.actionGroup}>
                      {/* Host / Receptionist Approve / Reject */}
                      {["AWAITING_HOST", "PENDING", "PENDING_HOST_APPROVAL", "SUBMITTED", "ESCALATED_DELEGATE", "ESCALATED_RECEPTION"].includes(v.status) && (
                        <>
                          <button
                            className={styles.btnApprove}
                            disabled={actingId === v.id}
                            onClick={() => handleAction(v.id, "APPROVE")}
                            title="Approve Visitor"
                          >
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button
                            className={styles.btnReject}
                            disabled={actingId === v.id}
                            onClick={() => handleAction(v.id, "REJECT")}
                            title="Reject Visitor"
                          >
                            <XCircle size={14} /> Reject
                          </button>
                          <button
                            className={styles.btnOverride}
                            onClick={() => openOverrideModal(v)}
                            title="Supervisor Override"
                          >
                            <ShieldAlert size={14} /> Override
                          </button>
                        </>
                      )}

                      {/* Gate Check In */}
                      {v.status === "APPROVED" && (
                        <button
                          className={styles.btnCheckIn}
                          disabled={actingId === v.id}
                          onClick={() => handleCheckIn(v.id)}
                          title="Check In at Gate"
                        >
                          <LogIn size={14} /> Check In
                        </button>
                      )}

                      {/* Gate Check Out */}
                      {v.status === "CHECKED_IN" && (
                        <button
                          className={styles.btnCheckOut}
                          disabled={actingId === v.id}
                          onClick={() => handleCheckOut(v.id)}
                          title="Check Out at Gate"
                        >
                          <LogOut size={14} /> Check Out
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            totalItems={filteredVisits.length}
            page={page}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      )}

      {/* Supervisor Override Modal */}
      <Modal
        isOpen={overrideModalOpen}
        onClose={() => setOverrideModalOpen(false)}
        title={`Security Supervisor Override: ${selectedVisit?.visitor_name}`}
      >
        <form onSubmit={submitOverride} className={styles.overrideForm}>
          <p className={styles.modalNotice}>
            Supervisor override permits gate entry when the host or receptionist is unavailable. This action is
            permanently recorded in the security audit logs.
          </p>

          <div className={styles.formGroup}>
            <label>Mandatory Override Reason *</label>
            <textarea
              required
              rows={3}
              className={styles.modalTextarea}
              placeholder="e.g. Host verbally confirmed via security intercom / Direct client meeting approved by MD"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
            />
          </div>

          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setOverrideModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingOverride}>
              {submittingOverride ? "Applying..." : "Confirm Override & Issue Pass"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Photo Zoom Modal */}
      <Modal isOpen={Boolean(previewPhotoUrl)} onClose={() => setPreviewPhotoUrl(null)} title="Live Visitor Photo">
        {previewPhotoUrl && (
          <div style={{ textAlign: "center", padding: "16px" }}>
            <img
              src={previewPhotoUrl}
              alt="Visitor"
              style={{ maxWidth: "100%", maxHeight: "400px", borderRadius: "10px", objectFit: "contain" }}
            />
          </div>
        )}
      </Modal>

      {/* QR Poster Generator Modal */}
      <PropertyQrPosterModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        societyId={societyId}
        propertyName={propertyName}
      />
    </div>
  );
}
